import { Canvas, useThree } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { OrthographicCamera, Vector3 } from 'three';

import {
  getObjectDefinition,
  getObjectRegistryVersion,
  subscribeObjectRegistry,
  type ObjectDefinition,
} from '../../content/registry/objectRegistry';
import { renderAsset } from '../../content/registry/objectRenderers';
import type { MapObjectInstance } from '../../content/schema/map';
import { MapObjectErrorBoundary } from '../components/MapObjectErrorBoundary';
import { useThumbnailStore } from './thumbnailStore';

const THUMBNAIL_PIXELS = 160;

function buildPreviewInstance(definition: ObjectDefinition): MapObjectInstance {
  // Slight Y rotation gives a 3/4 silhouette that reads much better than a
  // head-on view. Most props face forward along -Z in their neutral pose.
  const rotationY = Math.PI / 6;

  return {
    id: `thumbnail-${definition.id}`,
    assetId: definition.id,
    floor: 0,
    transform: {
      position: [0, 0, 0],
      rotation: [0, rotationY, 0],
      scale: [1, 1, 1],
    },
    params: definition.defaultParams ? { ...definition.defaultParams } : undefined,
  };
}

/**
 * Computes a 3/4-view orthographic camera that frames the object based on its
 * declared bounds and pivot convention.
 *
 * Known limitation: the camera always sits on the same fixed side (+X/+Z-ish)
 * of the target. For an object with real volume that's irrelevant - some
 * geometry always faces the camera. For a zero-depth, single-sided plane
 * (bounds size near 0 on one axis, material doubleSided: false - e.g. a flat
 * wall-panel decal) whose visible face happens to point away from that fixed
 * side, the camera looks squarely at the culled back face and captures a
 * blank/transparent thumbnail. Confirmed 2026-09-07 against three Kenney
 * "Retro Urban" wall panels in the shared library
 * (kenney/retro-urban/wall-a-flat, wall-a-flat-painted, wall-b-flat) - not a
 * capture failure, not a timing race, just this fixed-angle camera meeting a
 * flat, one-sided object oriented the other way. A proper fix would detect a
 * near-zero bounds axis and either choose the camera side from the object's
 * actual face-normal direction, or render doubleSided for this pass only
 * without touching the asset's real in-game material; out of scope for now
 * since it affects a handful of assets out of the full library.
 */
function computeCamera(definition: ObjectDefinition) {
  const [sx, sy, sz] = definition.bounds.size;
  const pivotBaseCenter = definition.editor.pivot === 'base-center';
  const centerY = pivotBaseCenter ? sy / 2 : 0;
  const offset = definition.editor.thumbnailTargetOffset ?? [0, 0, 0];
  const target = new Vector3(offset[0], centerY + offset[1], offset[2]);

  // Isometric-ish direction (elevation ~30°, yaw ~35°). Distance is derived
  // from the largest axis so tall lockers and wide shelves both fit.
  const longest = Math.max(sx, sy, sz, 0.5);
  const distance = longest * 2.4;
  const position = new Vector3(
    target.x + distance * Math.cos(Math.PI / 7) * Math.sin(Math.PI / 5),
    target.y + distance * Math.sin(Math.PI / 7) + sy * 0.2,
    target.z + distance * Math.cos(Math.PI / 7) * Math.cos(Math.PI / 5),
  );

  // Orthographic frustum: fit slightly larger than bounds diagonal for padding.
  const frustumRadius = Math.max(sx, sy, sz) * 0.72 + 0.35;

  return { position, target, frustumRadius };
}

/** One offscreen capture pass for an asset. Exported for the dev-only
 * library thumbnail baker, which drives its own queue and file output. */
export function ThumbnailStage({
  assetId,
  onCaptured,
  onFailed,
}: {
  assetId: string;
  onCaptured: (dataUrl: string) => void;
  onFailed: () => void;
}) {
  const { gl, scene, camera, size } = useThree();
  const definition = getObjectDefinition(assetId);
  const instance = useMemo(
    () => (definition ? buildPreviewInstance(definition) : null),
    [definition],
  );
  const cameraParams = useMemo(
    () => (definition ? computeCamera(definition) : null),
    [definition],
  );
  const captured = useRef(false);

  useEffect(() => {
    if (!cameraParams) {
      onFailed();
      return;
    }
    const { position, target, frustumRadius } = cameraParams;
    const cam = camera as OrthographicCamera;
    const aspect = size.width / size.height || 1;
    cam.left = -frustumRadius * aspect;
    cam.right = frustumRadius * aspect;
    cam.top = frustumRadius;
    cam.bottom = -frustumRadius;
    cam.near = 0.1;
    cam.far = frustumRadius * 12 + 10;
    cam.position.copy(position);
    cam.lookAt(target);
    cam.updateProjectionMatrix();
  }, [cameraParams, camera, size.width, size.height, onFailed]);

  useEffect(() => {
    if (!definition || !instance || !cameraParams) {
      onFailed();
      return;
    }
    captured.current = false;

    // The Suspense-wrapped renderAsset() content (e.g. an uploaded/library
    // GLTF via useGLTF) isn't in the scene graph yet on this first effect
    // run - it commits later, once the fetch resolves and Suspense reveals
    // it. A fixed short frame count is only "enough" by accident: on a warm
    // cache / fast local asset it is, but on a cold fetch (exactly the case
    // a thumbnail bake exists to handle - assets with no thumbnail yet) it
    // captures the scene before the model has been added, i.e. lights only,
    // producing a blank PNG with no error. Poll instead for the scene to
    // gain content beyond the 3 lights always present (ambient + 2
    // directional, declared below), with a generous timeout so a genuinely
    // contentless/erroring asset (which never grows the scene) still
    // captures - and fails visibly via a checkable blank thumbnail - rather
    // than hanging forever.
    //
    // LIGHT_CHILD_COUNT, not scene.children.length read here: for
    // synchronous content (e.g. Core pack's procedural geometry, which never
    // suspends) the object is already in the scene by the time this effect
    // runs post-commit, so a baseline captured now would already include it
    // - "hasContent" would never go true and every synchronous asset would
    // silently eat the full timeout instead of capturing immediately.
    const LIGHT_CHILD_COUNT = 3;
    const maxFrames = 180; // ~3s at 60fps; well past any reasonable glTF fetch+parse.
    let rafId = 0;
    let framesWaited = 0;
    let settleFrames = 0;
    const attempt = () => {
      if (captured.current) return;
      const hasContent = scene.children.length > LIGHT_CHILD_COUNT;
      const timedOut = framesWaited >= maxFrames;
      framesWaited += 1;
      if (hasContent || timedOut) {
        // A couple more frames once content appears so its first-frame
        // geometry/materials (e.g. a just-resolved texture) finish settling,
        // matching the original "wait a few frames" intent - just anchored
        // to when there's something to settle instead of a flat delay from
        // stage mount.
        if (hasContent && settleFrames < 2) {
          settleFrames += 1;
          rafId = requestAnimationFrame(attempt);
          return;
        }
        try {
          gl.render(scene, camera);
          const dataUrl = gl.domElement.toDataURL('image/png');
          captured.current = true;
          onCaptured(dataUrl);
        } catch (error) {
          console.warn('[thumbnails] capture failed for', assetId, error);
          onFailed();
        }
        return;
      }
      rafId = requestAnimationFrame(attempt);
    };
    rafId = requestAnimationFrame(attempt);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [assetId, gl, scene, camera, definition, instance, cameraParams, onCaptured, onFailed]);

  if (!instance) {
    return null;
  }

  return (
    <>
      <ambientLight intensity={0.75} />
      <directionalLight position={[4, 6, 3]} intensity={1.1} />
      <directionalLight position={[-3, 2, -4]} intensity={0.35} color="#8ea4c0" />
      {/* Async assets (uploaded glTF) suspend; the boundary + Suspense keep the
          offscreen canvas from crashing - a slow/failed model captures as the
          neutral gizmo rather than throwing. */}
      <Suspense fallback={null}>
        <MapObjectErrorBoundary instance={instance}>
          {renderAsset(instance)}
        </MapObjectErrorBoundary>
      </Suspense>
    </>
  );
}

/**
 * Invisible off-screen R3F canvas that renders each queued object once and
 * stores the PNG data URL back into the thumbnail store. Mount this near the
 * top of the editor tree.
 */
export function ThumbnailRenderer() {
  const initialise = useThumbnailStore((state) => state.initialise);
  const queueHead = useThumbnailStore((state) => state.queue[0] ?? null);
  const setThumbnail = useThumbnailStore((state) => state.setThumbnail);
  const markSkipped = useThumbnailStore((state) => state.markSkipped);

  // Re-initialise the queue when the registry changes so newly-registered
  // assets (e.g. an uploaded GLB) get a thumbnail. initialise() merges - it
  // keeps existing thumbnails and only queues unseen definitions.
  const registryVersion = useSyncExternalStore(subscribeObjectRegistry, getObjectRegistryVersion);
  useEffect(() => {
    initialise();
  }, [initialise, registryVersion]);

  if (!queueHead) {
    return null;
  }

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: THUMBNAIL_PIXELS,
        height: THUMBNAIL_PIXELS,
        pointerEvents: 'none',
        opacity: 0,
      }}
    >
      <Canvas
        key={queueHead}
        orthographic
        gl={{ preserveDrawingBuffer: true, antialias: true, alpha: true }}
        dpr={2}
        style={{ width: THUMBNAIL_PIXELS, height: THUMBNAIL_PIXELS }}
        frameloop="demand"
      >
        <ThumbnailStage
          assetId={queueHead}
          onCaptured={(dataUrl) => setThumbnail(queueHead, dataUrl)}
          onFailed={() => markSkipped(queueHead)}
        />
      </Canvas>
    </div>
  );
}
